<template>
  <div
    class="w-full bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-8"
    style="overflow: hidden; box-sizing: border-box"
  >
    <!-- Instructions Section -->
    <div
      v-if="!hideInstructions"
      class="bg-gradient-to-r from-yellow-50 to-amber-50 border-b-2 border-black p-6"
    >
      <div class="flex items-start gap-4">
        <div class="flex-shrink-0">
          <div class="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
            <svg
              class="w-6 h-6 text-gray-900"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-bold text-gray-900 mb-3">How to Generate AI Study Materials</h3>
          <div class="space-y-2.5 text-sm text-gray-800">
            <p class="flex items-start gap-2">
              <span class="font-bold text-amber-700 mt-0.5">1.</span>
              <span
                >Go to the <strong>"Session History"</strong> tab and find your completed
                sessions.</span
              >
            </p>
            <p class="flex items-start gap-2">
              <span class="font-bold text-amber-700 mt-0.5">2.</span>
              <span
                >Click the <strong>"Generate AI Summary"</strong> button on any completed
                session.</span
              >
            </p>
            <p class="flex items-start gap-2">
              <span class="font-bold text-amber-700 mt-0.5">3.</span>
              <span
                >Upload your session notes or paste text, then select Summary or Flashcards.</span
              >
            </p>
            <p class="flex items-start gap-2">
              <span class="font-bold text-amber-700 mt-0.5">4.</span>
              <span>Your AI-generated materials will appear here, linked to your session!</span>
            </p>
          </div>
          <div class="mt-4 p-3 bg-white rounded-lg border-2 border-yellow-400">
            <p class="text-xs text-gray-700 flex items-center gap-2">
              <svg
                class="w-4 h-4 text-amber-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                ><strong>Tip:</strong> AI materials are automatically saved to your library and
                linked to the session's subject.</span
              >
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-blue-50 p-6 rounded-t-lg">
      <div class="flex items-center mb-1">
        <div class="w-8 h-8 rounded-lg flex items-center justify-start">
          <DocumentTextIcon class="w-6 h-6 text-blue-800" />
        </div>
        <h2 class="text-2xl font-semibold text-blue-900">Your AI-Generated Study Materials</h2>
      </div>
      <p class="text-blue-800 font-semibold">
        Summaries, flashcards, and quiz questions from your notes.
      </p>
    </div>
    <div class="p-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Loading Study Materials...</h3>
        <p class="text-gray-500">Please wait while we fetch your AI-generated content.</p>
      </div>

      <!-- Not Authenticated State -->
      <div v-else-if="!currentUserId" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          Please log in to view your AI-generated study materials.
        </p>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Error Loading Study Materials</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          {{ errorMessage }}
        </p>
        <button
          @click="retryLoading"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="materials.length === 0" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <DocumentTextIcon class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Study Materials Yet</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          You haven't generated any AI-powered study materials yet. Upload your notes above to
          create summaries, flashcards, or quiz questions.
        </p>
      </div>

      <!-- Study Materials Grid -->
      <div class="max-w-full overflow-hidden">
        <div
          v-if="materials.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 items-stretch"
        >
          <div
            v-for="material in displayedMaterials"
            :key="material.id"
            class="max-w-[500px] w-full mx-auto"
          >
            <AIStudyMaterialCard :material="material" @view="handleView" @export="handleExport" />
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="materials.length > 0" class="flex items-center justify-between">
        <div class="text-sm text-gray-600">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ materials.length }} items
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="border border-blue-800 rounded-md px-3 py-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 border border-gray-400 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-3 py-1 rounded-md border border-blue-800',
              page === currentPage ? 'bg-blue-600 text-white' : 'text-gray-700  hover:bg-gray-100',
            ]"
          >
            {{ page }}
          </button>
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="border border-blue-800 rounded-md px-3 py-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 border border-gray-400 disabled:cursor-not-allowed cursor-pointer"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Study Material Modal -->
  <AIStudyMaterialModal :material="selectedMaterial" :is-open="isModalOpen" @close="closeModal" />
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import AIStudyMaterialCard from "./AIStudyMaterialCard.vue";
import AIStudyMaterialModal from "./AIStudyMaterialModal.vue";
import { DocumentTextIcon } from "@heroicons/vue/24/outline";
import { auth, db, collection, query, where, onSnapshot, orderBy } from "@/firebase";
import type { SummaryRecord } from "@/services/summaryService";
import type { FlashcardRecord } from "@/services/flashcardService";
import { onAuthStateChanged, type User } from "firebase/auth";
import type { QuerySnapshot, DocumentData } from "firebase/firestore";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";
import { htmlPdfExportService as pdfExportService } from "@/utils/htmlPdfExport";

interface AIStudyMaterial {
  id: string;
  title: string;
  type: "Summary" | "Flashcards" | "Quiz";
  subject: string;
  source: string;
  filename?: string; // Topic/filename for display
  date: Date | string; // Allow both Date object and string
  description: string;
  keyPoints: string;
  keyPointsList?: string[];
  flashcards?: Array<{ front: string; back: string }>; // Include flashcard data for PDF export
  sources?: string[]; // Include sources for PDF export
  student_notes_url?: string; // Firebase Storage URL for original student notes/file
}

interface Props {
  itemsPerPage?: number;
  hideInstructions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemsPerPage: 10,
  hideInstructions: false,
});

// Reactive data for Firebase integration
const materials = ref<AIStudyMaterial[]>([]);
const isLoading = ref(true);
const currentUserId = ref<string | null>(null);
const hasError = ref(false);
const errorMessage = ref("");
let unsubscribeSummariesFirestore: (() => void) | null = null;
let unsubscribeFlashcardsFirestore: (() => void) | null = null;
let unsubscribeAuth: (() => void) | null = null;

const currentPage = ref(1);
const isModalOpen = ref(false);
const selectedMaterial = ref<AIStudyMaterial | null>(null);

// Helper function to transform SummaryRecord to AIStudyMaterial
const transformSummaryToMaterial = (summary: SummaryRecord): AIStudyMaterial => {
  // Format date with time
  const date = summary.date_generated?.toDate ? summary.date_generated.toDate() : new Date();

  // Get source name from source_type or use a default
  const sourceMap = {
    pdf: "PDF Document",
    txt: "Text File",
    docx: "Word Document",
    note: "Text Notes",
  };

  // Limit description to 15 words
  const words = summary.summary_text.split(" ");
  const limitedDescription =
    words.length > 15 ? words.slice(0, 15).join(" ") + "..." : summary.summary_text;

  return {
    id: summary.summary_id || Math.random().toString(36).substr(2, 9),
    title: summary.topic || "AI Generated Summary",
    type: "Summary", // All items from summaries collection are summaries
    subject: summary.subject || "General",
    source: sourceMap[summary.source_type] || "Unknown Source",
    filename: summary.filename || undefined, // Use filename
    date: date, // Pass the full Date object for formatting in the card
    description: limitedDescription,
    keyPoints: summary.key_points
      ? `${summary.key_points.length} key points identified`
      : "No key points available",
    keyPointsList: summary.key_points || [],
    student_notes_url: summary.student_notes_url || undefined, // Include download URL if available
  };
};

// Helper function to transform FlashcardRecord to AIStudyMaterial
const transformFlashcardToMaterial = (flashcard: FlashcardRecord): AIStudyMaterial => {
  // Format date with time
  const date = flashcard.date_generated?.toDate ? flashcard.date_generated.toDate() : new Date();

  // Get source name from source_type or use a default
  const sourceMap = {
    pdf: "PDF Document",
    txt: "Text File",
    docx: "Word Document",
    note: "Text Notes",
  };

  // Create description from first few flashcards
  const firstCard = flashcard.flashcard_data?.[0];
  const description = firstCard
    ? `${firstCard.front}...`
    : `Generated ${flashcard.flashcard_data?.length || 0} flashcards`;

  return {
    id: flashcard.flashcards_id || Math.random().toString(36).substr(2, 9),
    title: flashcard.topic || "AI Generated Flashcards",
    type: "Flashcards", // All items from flashcards collection are flashcards
    subject: flashcard.subject || "General",
    source: sourceMap[flashcard.source_type] || "Unknown Source",
    filename: flashcard.filename || flashcard.topic || undefined, // Use filename if available, fall back to topic
    date: date, // Pass the full Date object for formatting in the card
    description: description,
    keyPoints: flashcard.flashcard_data
      ? `${flashcard.flashcard_data.length} flashcards generated`
      : "No flashcards available",
    keyPointsList: flashcard.flashcard_data?.map((card) => `${card.front}: ${card.back}`) || [],
    flashcards: flashcard.flashcard_data || [], // Include flashcard data for PDF export
    sources: flashcard.sources || [], // Include sources for PDF export
    student_notes_url: flashcard.student_notes_url || undefined, // Include download URL if available
  };
};

// Set up Firebase listeners
const setupFirebaseListeners = () => {
  logDebug("Setting up Firebase authentication listener");
  // Listen to auth state changes
  unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
    if (user) {
      logInfo("User authenticated, setting up listeners for both summaries and flashcards");
      currentUserId.value = user.uid;
      setupSummariesListener();
      setupFlashcardsListener();
    } else {
      logInfo("User not authenticated, clearing materials");
      currentUserId.value = null;
      materials.value = [];
      isLoading.value = false;
      // Clean up existing listeners
      if (unsubscribeSummariesFirestore) {
        unsubscribeSummariesFirestore();
        unsubscribeSummariesFirestore = null;
      }
      if (unsubscribeFlashcardsFirestore) {
        unsubscribeFlashcardsFirestore();
        unsubscribeFlashcardsFirestore = null;
      }
    }
  });
};

// Combined materials from both summaries and flashcards
const summariesData = ref<AIStudyMaterial[]>([]);
const flashcardsData = ref<AIStudyMaterial[]>([]);

// Combine and sort materials from both collections
const updateCombinedMaterials = () => {
  const combined = [...summariesData.value, ...flashcardsData.value];
  // Sort by date, newest first
  combined.sort((a, b) => {
    const dateA = a.date instanceof Date ? a.date : new Date(a.date);
    const dateB = b.date instanceof Date ? b.date : new Date(b.date);
    return dateB.getTime() - dateA.getTime();
  });
  materials.value = combined;
  logDebug("Combined materials updated", {
    summaries: summariesData.value.length,
    flashcards: flashcardsData.value.length,
    total: materials.value.length,
  });
};

const setupSummariesListener = () => {
  if (!currentUserId.value) return;

  logDebug("Setting up Firestore summaries listener with orderBy");

  try {
    const summariesRef = collection(db, "summaries");
    const summariesQuery = query(
      summariesRef,
      where("user_id", "==", currentUserId.value),
      orderBy("date_generated", "desc")
    );

    unsubscribeSummariesFirestore = onSnapshot(
      summariesQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Summaries snapshot received", { count: snapshot.size });
        console.log("📥 FIRESTORE LISTENER: Summaries snapshot received", {
          count: snapshot.size,
          timestamp: new Date().toISOString(),
        });

        const summaries: SummaryRecord[] = [];

        snapshot.forEach((doc) => {
          const docData = doc.data();
          summaries.push({
            summary_id: doc.id,
            ...(docData as Omit<SummaryRecord, "summary_id">),
          });
          console.log("📄 Summary Document:", {
            id: doc.id.substring(0, 8) + "***",
            topic: docData.topic,
            subject: docData.subject,
          });
        });

        // Transform summaries to materials
        summariesData.value = summaries.map(transformSummaryToMaterial);
        updateCombinedMaterials();
        isLoading.value = false;
        hasError.value = false;
        logSuccess("Summaries loaded successfully", { count: summariesData.value.length });
      },
      (error) => {
        // If orderBy fails due to missing index, try without it
        if (error.code === "failed-precondition") {
          logInfo("Composite index not found for summaries, using client-side sorting instead");
          setupSummariesListenerWithoutOrder();
        } else {
          logError("Error fetching summaries with orderBy", error);
          isLoading.value = false;
          hasError.value = true;
          errorMessage.value = "Failed to load study materials. Please try refreshing the page.";
        }
      }
    );
  } catch (error) {
    logError("Error setting up summaries listener", error);
    isLoading.value = false;
    hasError.value = true;
    errorMessage.value = "Failed to connect to database. Please check your internet connection.";
  }
};

const setupSummariesListenerWithoutOrder = () => {
  if (!currentUserId.value) return;

  logInfo("Setting up Firestore summaries listener without orderBy (client-side sorting)");

  try {
    const summariesRef = collection(db, "summaries");
    const summariesQuery = query(summariesRef, where("user_id", "==", currentUserId.value));

    unsubscribeSummariesFirestore = onSnapshot(
      summariesQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Summaries snapshot received (client-side sort)", { count: snapshot.size });

        const summaries: SummaryRecord[] = [];

        snapshot.forEach((doc) => {
          const docData = doc.data();
          summaries.push({
            summary_id: doc.id,
            ...(docData as Omit<SummaryRecord, "summary_id">),
          });
          console.log("📄 Summary Document:", {
            id: doc.id.substring(0, 8) + "***",
            topic: docData.topic,
            subject: docData.subject,
          });
        });

        // Sort client-side by date_generated (most recent first)
        summaries.sort((a, b) => {
          const aTime = a.date_generated?.seconds || 0;
          const bTime = b.date_generated?.seconds || 0;
          return bTime - aTime;
        });

        // Transform summaries to materials
        summariesData.value = summaries.map(transformSummaryToMaterial);
        updateCombinedMaterials();
        isLoading.value = false;
        hasError.value = false;
        logSuccess("Summaries loaded successfully with client-side sorting", {
          count: summariesData.value.length,
        });
      },
      (error) => {
        logError("Error fetching summaries without orderBy", error);
        isLoading.value = false;
        hasError.value = true;
        errorMessage.value = "Failed to load study materials. Please try refreshing the page.";
      }
    );
  } catch (error) {
    logError("Error setting up summaries listener without orderBy", error);
    isLoading.value = false;
    hasError.value = true;
    errorMessage.value = "Failed to connect to database. Please check your internet connection.";
  }
};

const setupFlashcardsListener = () => {
  if (!currentUserId.value) return;

  logDebug("Setting up Firestore flashcards listener with orderBy");

  try {
    const flashcardsRef = collection(db, "flashcards");
    const flashcardsQuery = query(
      flashcardsRef,
      where("user_id", "==", currentUserId.value),
      orderBy("date_generated", "desc")
    );

    unsubscribeFlashcardsFirestore = onSnapshot(
      flashcardsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Flashcards snapshot received", { count: snapshot.size });
        console.log("📥 FIRESTORE LISTENER: Flashcards snapshot received", {
          count: snapshot.size,
          timestamp: new Date().toISOString(),
        });

        const flashcards: FlashcardRecord[] = [];

        snapshot.forEach((doc) => {
          const docData = doc.data();
          flashcards.push({
            flashcards_id: doc.id,
            ...(docData as Omit<FlashcardRecord, "flashcards_id">),
          });
          console.log("🃏 Flashcard Document:", {
            id: doc.id.substring(0, 8) + "***",
            topic: docData.topic,
            subject: docData.subject,
            cardCount: docData.flashcard_data?.length || 0,
          });
        });

        // Transform flashcards to materials
        flashcardsData.value = flashcards.map(transformFlashcardToMaterial);
        updateCombinedMaterials();
        logSuccess("Flashcards loaded successfully", { count: flashcardsData.value.length });
      },
      (error) => {
        // If orderBy fails due to missing index, try without it
        if (error.code === "failed-precondition") {
          logInfo("Composite index not found for flashcards, using client-side sorting instead");
          setupFlashcardsListenerWithoutOrder();
        } else {
          logError("Error fetching flashcards with orderBy", error);
          // Don't set error state here since summaries might still work
          logWarn("Flashcards unavailable, continuing with summaries only");
        }
      }
    );
  } catch (error) {
    logError("Error setting up flashcards listener", error);
    logWarn("Flashcards unavailable, continuing with summaries only");
  }
};

const setupFlashcardsListenerWithoutOrder = () => {
  if (!currentUserId.value) return;

  logInfo("Setting up Firestore flashcards listener without orderBy (client-side sorting)");

  try {
    const flashcardsRef = collection(db, "flashcards");
    const flashcardsQuery = query(flashcardsRef, where("user_id", "==", currentUserId.value));

    unsubscribeFlashcardsFirestore = onSnapshot(
      flashcardsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Flashcards snapshot received (client-side sort)", { count: snapshot.size });

        const flashcards: FlashcardRecord[] = [];

        snapshot.forEach((doc) => {
          const docData = doc.data();
          flashcards.push({
            flashcards_id: doc.id,
            ...(docData as Omit<FlashcardRecord, "flashcards_id">),
          });
          console.log("🃏 Flashcard Document:", {
            id: doc.id.substring(0, 8) + "***",
            topic: docData.topic,
            subject: docData.subject,
            cardCount: docData.flashcard_data?.length || 0,
          });
        });

        // Sort client-side by date_generated (most recent first)
        flashcards.sort((a, b) => {
          const aTime = a.date_generated?.seconds || 0;
          const bTime = b.date_generated?.seconds || 0;
          return bTime - aTime;
        });

        // Transform flashcards to materials
        flashcardsData.value = flashcards.map(transformFlashcardToMaterial);
        updateCombinedMaterials();
        logSuccess("Flashcards loaded successfully with client-side sorting", {
          count: flashcardsData.value.length,
        });
      },
      (error) => {
        logError("Error fetching flashcards without orderBy", error);
        logWarn("Flashcards unavailable, continuing with summaries only");
      }
    );
  } catch (error) {
    logError("Error setting up flashcards listener without orderBy", error);
    logWarn("Flashcards unavailable, continuing with summaries only");
  }
};

// Lifecycle hooks
onMounted(() => {
  logInfo("AIStudyMaterialsGrid mounted, initializing Firebase listeners");
  setupFirebaseListeners();
});

onUnmounted(() => {
  // Clean up listeners when component is unmounted
  if (unsubscribeSummariesFirestore) {
    unsubscribeSummariesFirestore();
  }
  if (unsubscribeFlashcardsFirestore) {
    unsubscribeFlashcardsFirestore();
  }
  if (unsubscribeAuth) {
    unsubscribeAuth();
  }
});

const totalPages = computed(() => Math.ceil(materials.value.length / props.itemsPerPage));

const startIndex = computed(() => (currentPage.value - 1) * props.itemsPerPage);
const endIndex = computed(() =>
  Math.min(startIndex.value + props.itemsPerPage, materials.value.length)
);

const displayedMaterials = computed(() => materials.value.slice(startIndex.value, endIndex.value));

const goToPage = (page: number) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleView = (material: AIStudyMaterial) => {
  selectedMaterial.value = material;
  isModalOpen.value = true;
  emit("view", material);
};

const handleExport = async (material: AIStudyMaterial) => {
  try {
    logInfo("Starting PDF export for study material", {
      materialId: material.id.substring(0, 8) + "***",
      type: material.type,
    });

    await pdfExportService.exportMaterialToPDF(material);

    logSuccess("PDF export completed successfully");
    emit("export", material);
  } catch (error) {
    logError("Failed to export study material to PDF", error);

    // You might want to show a user-friendly error message here
    // For now, we'll just log the error and still emit the event
    emit("export", material);
  }
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedMaterial.value = null;
};

const retryLoading = () => {
  logInfo("User requested retry, resetting error state and restarting listeners");
  hasError.value = false;
  errorMessage.value = "";
  isLoading.value = true;
  setupFirebaseListeners();
};

const emit = defineEmits<{
  view: [material: AIStudyMaterial];
  export: [material: AIStudyMaterial];
}>();
</script>
